import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  PageHeader,
  ScreenLoading,
  Table,
  TableContainer,
  useFeatures,
  useModal,
  Modal,
  addActionColumn,
  Stack,
} from '@ui-components/neo.tax';
import { useMemo, useState } from 'react';

import { useCompanies } from '@neo.tax/shared/hooks';
import { GetCompaniesQueryCompany } from '@neo.tax/shared/hooks/useCompanies';
import { useAnalyticId } from '@neo.tax/shared/state';

import { CreateCompany } from '../create-company';
import { DeleteCompany } from '../delete-company';
import { KeywordSearch, useKeywordSearch } from '../keyword-search';
import { useSorting, makeSortComparator, SortPopover } from '../sort-popover';
import { companyListColumns, sortableColumns } from './company-list.columns';
import { mapSortValues } from './company-list.utils';

export const CompanyList = ({ allowCreateCompany }: { allowCreateCompany: boolean }): React.ReactElement | null => {
  const { companies, loading: isLoadingCompanies } = useCompanies();
  const { isOpen, setIsOpen, toggle: toggleModal } = useModal();
  const [companyToDelete, setCompanyToDelete] = useState<GetCompaniesQueryCompany>();
  const createCompanyId = useAnalyticId(AnalyticIdentifier.RTC_ACCOUNTANT_OPEN_CREATE_COMPANY_MODAL);
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const sortState = useSorting({ columns: sortableColumns, initialSorting: [] });
  const sorting = sortState.sorts.map((s) => ({ field: s.column, direction: s.direction }));

  const onCancelToggleModal = (): void => {
    setCompanyToDelete(undefined);
  };

  const deleteCompanyAction = addActionColumn<GetCompaniesQueryCompany>({
    cellRender: ({ row }) => {
      if (!allowCreateCompany) return <div />;
      const onClick = (): void => {
        setCompanyToDelete(row.original);
      };
      return (
        <Button onClick={onClick} size="sm" variant="action" rounded="regular">
          Delete
        </Button>
      );
    },
  });
  const features = useFeatures<GetCompaniesQueryCompany>(deleteCompanyAction);

  const data = useMemo(() => {
    if (!companies) {
      return [];
    }
    const keyword = searchKeyword?.toLowerCase();
    const filteredCompanies = keyword
      ? companies.filter(
          (company) =>
            company.legalName.toLowerCase().includes(keyword) || company.shortName.toLowerCase().includes(keyword),
        )
      : companies;
    const sortedCompanies =
      sorting.length === 0
        ? filteredCompanies
        : [...filteredCompanies].sort(makeSortComparator(sorting, mapSortValues));
    return sortedCompanies;
  }, [companies, searchKeyword, sorting]);

  if (isLoadingCompanies) return <ScreenLoading />;
  return (
    <Stack direction="column" gap={8} fullHeight>
      <PageHeader
        subtitle="Welcome to your Neo.Tax Dashboard. Select a company to get started."
        title="Companies"
        trailingContent={
          allowCreateCompany ? (
            <Button variant="primary" rounded="regular" size="base" onClick={toggleModal} id={createCompanyId}>
              Create Company
            </Button>
          ) : null
        }
      />
      <Stack gap={5}>
        <KeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
        <SortPopover itemsDescription="companies" {...sortState} />
      </Stack>
      <TableContainer>
        <Table<GetCompaniesQueryCompany> columns={companyListColumns} data={data} {...features} />
      </TableContainer>

      <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
        <CreateCompany close={toggleModal} />
      </Modal>
      <Modal isOpen={!!companyToDelete} setIsOpen={onCancelToggleModal}>
        <DeleteCompany close={onCancelToggleModal} company={companyToDelete} />
      </Modal>
    </Stack>
  );
};
